/**
 * Instagram 新規投稿 Modal(spec `instagram_new_post_modal_minimal_spec.md`)。
 *
 * 构成(SPEC §3):
 *   - InstagramPostComposerModal.tsx(本文件 — 親: state 管理 + submit)
 *   - InstagramMediaPicker.tsx    左欄 — Media Library + Upload
 *   - InstagramAiAssistant.tsx    右欄上 — AI caption / hashtag 生成
 *
 * State は spec §14 の単一オブジェクトに集約。submit ロジックは
 * 既存 `instagramApi.createDraft` / `publishDraft` のみ呼び出す。
 * Backend / API / DB / Hook シグネチャは一切触らない。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ImagePlus,
  Loader2,
  Send,
  Sparkles,
  X,
} from "lucide-react";

import { instagramApi } from "../../../api/instagram";
import {
  InstagramMediaPicker,
  uploadPendingMediaFiles,
  type PendingMedia,
  type SelectedInstagramMedia,
} from "./InstagramMediaPicker";
import { InstagramAiAssistant } from "./InstagramAiAssistant";

// ---------------------------------------------------------------------------
// 定数(spec §9.1 — Backend が本当に対応している content_type のみ)
// 既存 API は単一画像のみ対応(media_publish)。他タイプ選択 UI は出さない。
// ---------------------------------------------------------------------------

const CONTENT_TYPE_OPTIONS: { key: "IMAGE"; label: string }[] = [
  { key: "IMAGE", label: "画像" },
];

type PublishMode = "draft" | "now";

export type InstagramComposerState = {
  managementTitle: string;
  contentType: "IMAGE";
  selectedMedia: SelectedInstagramMedia[];
  pendingMedia: PendingMedia[];
  caption: string;
  hashtags: string[];
  publishMode: PublishMode;
  submitting: boolean;
  uploading: boolean;
  error: string | null;
  titleError: string | null;
  mediaError: string | null;
};

const INITIAL_STATE: InstagramComposerState = {
  managementTitle: "",
  contentType: "IMAGE",
  selectedMedia: [],
  pendingMedia: [],
  caption: "",
  hashtags: [],
  publishMode: "draft",
  submitting: false,
  uploading: false,
  error: null,
  titleError: null,
  mediaError: null,
};

// ---------------------------------------------------------------------------
// Caption + Hashtag 結合(spec §9.3)
// Backend に hashtag 専用 field を追加せず、Caption 末尾へ連結して送る。
// ---------------------------------------------------------------------------

export function buildFinalCaption(caption: string, hashtags: string[]): string {
  const normalized = hashtags
    .map((tag) => tag.trim().replace(/^#+/, ""))
    .filter(Boolean)
    .map((tag) => `#${tag}`);

  if (normalized.length === 0) return caption.trim();
  return `${caption.trim()}\n\n${normalized.join(" ")}`.trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstagramPostComposerModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [state, setState] = useState<InstagramComposerState>(INITIAL_STATE);

  // Esc で閉じる(spec §5.3)。未保存変更がある場合は親に通知するだけ。
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Body scroll lock(spec §5.1)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const setField = useCallback(
    <K extends keyof InstagramComposerState>(
      key: K,
      value: InstagramComposerState[K],
    ) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleClose = useCallback(() => {
    // debug §3.5: object URL 解放
    state.pendingMedia.forEach((it) => URL.revokeObjectURL(it.previewUrl));
    onClose();
    // 关闭后清空 state,下次打开时干净
    setState(INITIAL_STATE);
  }, [onClose, state.pendingMedia]);

  // spec §16 + debug §5 validation
  const validation = useMemo(() => {
    const hasSelected = state.selectedMedia.length > 0;
    const hasPending = state.pendingMedia.length > 0;
    if (state.submitting) return { ok: false, message: "送信中です..." };
    if (state.uploading) return { ok: false, message: "アップロード中です..." };
    if (!hasSelected && !hasPending) {
      return { ok: false, message: "投稿するメディアを選択してください。" };
    }
    return { ok: true, message: null };
  }, [state.selectedMedia, state.pendingMedia, state.submitting, state.uploading]);

  // debug §5: submit 時にのみ管理タイトルとメディアの両方を検証
  const submitRef = useRef(false);
  const handleSubmit = useCallback(
    async (mode: PublishMode) => {
      if (submitRef.current) return;
      if (state.submitting || state.uploading) return;

      // 1. タイトル検証
      if (!state.managementTitle.trim()) {
        setState((prev) => ({
          ...prev,
          titleError: "管理用タイトルを入力してください。",
        }));
        return;
      }
      // 2. メディア検証
      if (state.selectedMedia.length === 0 && state.pendingMedia.length === 0) {
        setState((prev) => ({
          ...prev,
          mediaError: "投稿するメディアを選択してください。",
        }));
        return;
      }

      submitRef.current = true;
      setState((prev) => ({
        ...prev,
        submitting: true,
        error: null,
        titleError: null,
        mediaError: null,
        publishMode: mode,
      }));

      try {
        // debug §6: pending ファイルを真 Upload(rename + WP API)
        const mediaUrls = await uploadPendingMediaFiles({
          pendingMedia: state.pendingMedia,
          managementTitle: state.managementTitle,
          existing: state.selectedMedia,
          setPendingMedia: (next) =>
            setState((prev) => ({ ...prev, pendingMedia: next })),
          setSelected: (next) =>
            setState((prev) => ({ ...prev, selectedMedia: next })),
          setUploading: (u) =>
            setState((prev) => ({ ...prev, uploading: u })),
        });

        const media = mediaUrls[0];
        if (!media?.publicUrl) {
          throw new Error("公開 URL が取得できませんでした。");
        }

        const payload = {
          image_url: media.publicUrl,
          caption: buildFinalCaption(state.caption, state.hashtags),
          content_type: state.contentType,
        };

        const draft = await instagramApi.createDraft(payload);

        if (mode === "now") {
          await instagramApi.publishDraft(draft.id);
        }

        onSaved();
        handleClose();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setState((prev) => ({
          ...prev,
          submitting: false,
          error: `保存に失敗しました: ${msg}`,
        }));
      } finally {
        submitRef.current = false;
      }
    },
    [state, onSaved, handleClose],
  );

  if (!open) return null;

  return (
    <div
      className="ig-composer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ig-composer-title"
    >
      <div className="ig-composer-modal" onClick={(e) => e.stopPropagation()}>
        {/* ---- Header ---- */}
        <header className="ig-composer-header">
          <div>
            <h2 id="ig-composer-title" className="ig-composer-title">
              新規投稿を作成
            </h2>
            <p className="ig-composer-subtitle">
              Instagramに投稿するコンテンツを作成します
            </p>
          </div>
          <button
            type="button"
            className="ig-composer-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </header>

        {/* ---- Body ---- */}
        <div className="ig-composer-body">
          {/* 左:メディア選択(spec §7) */}
          <section className="ig-composer-column" aria-label="メディア選択">
            <h3 className="ig-composer-column-title">1. メディアを選択</h3>
            <InstagramMediaPicker
              managementTitle={state.managementTitle}
              selected={state.selectedMedia}
              pendingMedia={state.pendingMedia}
              onPendingChange={(next) => setField("pendingMedia", next)}
              onChange={(next) => setField("selectedMedia", next)}
              onUploadingChange={(uploading) => setField("uploading", uploading)}
            />
            {state.mediaError ? (
              <div className="ig-ai-error" style={{ marginTop: 10 }}>
                {state.mediaError}
              </div>
            ) : null}
          </section>

          {/* 中央:投稿内容(spec §9) */}
          <section className="ig-composer-column" aria-label="投稿内容">
            <h3 className="ig-composer-column-title">2. 投稿内容を編集</h3>

            {/* 管理用タイトル(spec §8) */}
            <div style={{ marginBottom: 14 }}>
              <label className="ig-field-label">
                管理用タイトル
                <span className="ig-field-required">*</span>
              </label>
              <input
                className="ig-input"
                placeholder="例:夏キャンペーン商品紹介"
                value={state.managementTitle}
                onChange={(e) => {
                  setField("managementTitle", e.target.value);
                  if (state.titleError) setField("titleError", null);
                }}
              />
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ig-muted)",
                  marginTop: 4,
                }}
              >
                ※ アップロード時のファイル名に使用します。Instagram には保存されません。
              </div>
              {state.titleError ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ig-danger)",
                    marginTop: 6,
                  }}
                >
                  {state.titleError}
                </div>
              ) : null}
            </div>

            {/* Content Type(spec §9.1 — Backend 実対応のみ) */}
            <div style={{ marginBottom: 14 }}>
              <label className="ig-field-label">コンテンツタイプ</label>
              <div className="ig-ai-tone-group">
                {CONTENT_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`ig-ai-tone-pill is-active`}
                    disabled
                    title="既存 API は画像のみ対応"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Caption(spec §9.2) */}
            <div style={{ marginBottom: 14 }}>
              <label className="ig-field-label">キャプション</label>
              <textarea
                className="ig-caption-input"
                placeholder="投稿の本文を入力...&#10;&#10;ハッシュタグは下の入力欄に追加してください。"
                value={state.caption}
                onChange={(e) => setField("caption", e.target.value)}
              />
            </div>

            {/* Hashtag(spec §9.3) */}
            <HashtagEditor
              hashtags={state.hashtags}
              onChange={(next) => setField("hashtags", next)}
            />
          </section>

          {/* 右:AI + 公開設定(spec §10 + §11) */}
          <section
            className="ig-composer-column ig-composer-column--assistant"
            aria-label="AI アシスタントと公開設定"
          >
            <h3 className="ig-composer-column-title">3. AI サポート・公開設定</h3>

            <InstagramAiAssistant
              managementTitle={state.managementTitle}
              caption={state.caption}
              selectedMedia={state.selectedMedia[0]}
              onApplyCaption={(text) => setField("caption", text)}
              onApplyHashtags={(tags) => setField("hashtags", tags)}
            />

            {/* 公開設定(spec §11 — 予約 option は disabled) */}
            <div style={{ marginTop: 24 }}>
              <h3 className="ig-composer-column-title">4. 公開設定</h3>
              <div className="ig-publish-mode">
                <label
                  className={`ig-publish-mode-row ${
                    state.publishMode === "draft" ? "is-active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="ig-publish-mode"
                    checked={state.publishMode === "draft"}
                    onChange={() => setField("publishMode", "draft")}
                  />
                  <span style={{ flex: 1 }}>下書きとして保存</span>
                </label>
                <label
                  className={`ig-publish-mode-row ${
                    state.publishMode === "now" ? "is-active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="ig-publish-mode"
                    checked={state.publishMode === "now"}
                    onChange={() => setField("publishMode", "now")}
                  />
                  <span style={{ flex: 1 }}>今すぐ公開</span>
                </label>
                <label className="ig-publish-mode-row is-disabled">
                  <input type="radio" name="ig-publish-mode" disabled />
                  <span style={{ flex: 1 }}>
                    日時を指定して予約
                    <span className="ig-publish-coming-soon" style={{ marginLeft: 8 }}>
                      近日対応予定
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {state.error ? (
              <div className="ig-ai-error" style={{ marginTop: 12 }}>
                <AlertTriangle size={11} style={{ marginRight: 4 }} />
                {state.error}
              </div>
            ) : null}
          </section>
        </div>

        {/* ---- Footer(spec §12) ---- */}
        <footer className="ig-composer-footer">
          <div className="ig-composer-footer-actions" style={{ marginRight: "auto" }}>
            <button
              type="button"
              className="ig-button"
              onClick={handleClose}
              disabled={state.submitting}
            >
              キャンセル
            </button>
          </div>

          <div className="ig-composer-footer-actions">
            <button
              type="button"
              className="ig-button ig-button--primary"
              onClick={() => handleSubmit("draft")}
              disabled={!validation.ok}
              title={
                validation.ok ? undefined : "メディアを1件以上選択してください"
              }
            >
              {state.submitting && state.publishMode === "draft" ? (
                <Loader2 size={14} className="ig-spin" />
              ) : (
                <ImagePlus size={14} />
              )}
              下書きを保存
            </button>
            <button
              type="button"
              className="ig-gradient-button"
              onClick={() => handleSubmit("now")}
              disabled={!validation.ok}
              title={
                validation.ok
                  ? undefined
                  : "メディアを1件以上選択してください"
              }
            >
              {state.submitting && state.publishMode === "now" ? (
                <Loader2 size={14} className="ig-spin" />
              ) : (
                <Send size={14} />
              )}
              今すぐ公開
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hashtag editor(中央カラム内 — spec §9.3)
// ---------------------------------------------------------------------------

function HashtagEditor({
  hashtags,
  onChange,
}: {
  hashtags: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim().replace(/^#+/, "").replace(/\s+/g, "");
    if (!v) return;
    if (hashtags.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...hashtags, v]);
    setDraft("");
  };

  const remove = (tag: string) => {
    onChange(hashtags.filter((t) => t !== tag));
  };

  return (
    <div>
      <label className="ig-field-label">ハッシュタグ</label>
      <div className="ig-hashtag-input-row">
        <input
          className="ig-input"
          placeholder="例: 新商品"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="ig-button"
          onClick={add}
          disabled={!draft.trim()}
          style={{ height: 40 }}
        >
          追加
        </button>
      </div>
      {hashtags.length > 0 ? (
        <div className="ig-hashtag-list">
          {hashtags.map((t) => (
            <span key={t} className="ig-hashtag-chip">
              #{t}
              <button
                type="button"
                onClick={() => remove(t)}
                aria-label={`${t} を削除`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "var(--ig-muted)" }}>
          ハッシュタグは保存時にキャプション末尾へ自動的に結合されます
        </div>
      )}
    </div>
  );
}